import { useCallback } from "react";
import axios from "axios";
import { vceFirestore, hvoFirestore } from "../../configs/FirebaseConfig";
import { getFirebaseIDTokenForShop } from "../../functions/firebase/firebase";
import { resolveShopApiUrl } from "../../functions/api/shopApi";
import {
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_SUPPLEMENTS_SUBCOLLECTION,
  FIRESTORE_HISTORY_SUBCOLLECTION,
} from "../../constants/FirestoreConstant";

const resolveFirestore = (shopSource) => (shopSource === "HVO" ? hvoFirestore : vceFirestore);

/**
 * CRUD for an order's `supplements` and `history` subcollections, plus the api calls needed
 * to turn a supplement into an actual Pay. payment link. Every api call goes to the order's
 * own shop api (VCE and HVO both support supplements).
 *
 * Supplements/history deliberately live in their own subcollection documents rather than as
 * array fields on the order - a status transition (open -> paid/cancelled) is then a single-
 * document update, which can never race with (and silently lose) a concurrent order edit or
 * webhook update the way a read-modify-write on a shared array field could.
 */
export const useSupplements = () => {
  /**
   * The one open (unpaid) additional-payment supplement for this order, if any.
   * Used to block further product edits until it's resolved (paid or cancelled) -
   * a Pay. transaction can't be corrected in amount once created.
   */
  const getOpenAdditionalPaymentSupplement = useCallback(async (order) => {
    const firestore = resolveFirestore(order.shopSource);
    const snapshot = await firestore
      .collection(FIRESTORE_ORDERS_TABLE)
      .doc(order.id)
      .collection(FIRESTORE_SUPPLEMENTS_SUBCOLLECTION)
      .where("type", "==", "additional_payment")
      .where("status", "==", "open")
      .limit(1)
      .get();
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  }, []);

  const addHistoryEntry = useCallback(async (order, entry) => {
    const firestore = resolveFirestore(order.shopSource);
    await firestore
      .collection(FIRESTORE_ORDERS_TABLE)
      .doc(order.id)
      .collection(FIRESTORE_HISTORY_SUBCOLLECTION)
      .add({ timestamp: Date.now(), ...entry });
  }, []);

  const createSupplement = useCallback(async (order, supplement) => {
    const firestore = resolveFirestore(order.shopSource);
    const docRef = await firestore
      .collection(FIRESTORE_ORDERS_TABLE)
      .doc(order.id)
      .collection(FIRESTORE_SUPPLEMENTS_SUBCOLLECTION)
      .add({
        createdAt: Date.now(),
        status: "open",
        payTransactionId: null,
        paymentUrl: null,
        paidAt: null,
        orderId: order.id,
        orderReference: order.orderId,
        shopSource: order.shopSource,
        customerName: `${order.customer?.firstName || ""} ${order.customer?.lastName || ""}`.trim(),
        customerFirstName: order.customer?.firstName || null,
        customerLastName: order.customer?.lastName || null,
        customerEmail: order.customer?.email || null,
        ...supplement,
      });
    return docRef.id;
  }, []);

  const updateSupplement = useCallback(async (order, supplementId, updates) => {
    const firestore = resolveFirestore(order.shopSource);
    await firestore
      .collection(FIRESTORE_ORDERS_TABLE)
      .doc(order.id)
      .collection(FIRESTORE_SUPPLEMENTS_SUBCOLLECTION)
      .doc(supplementId)
      .update(updates);
  }, []);

  /**
   * Creates the Pay. transaction for a supplement on that order's own shop api - only that
   * api can read the order's Firestore project and store the transaction id on the supplement.
   */
  const createPaymentLinkForSupplement = useCallback(async (order, supplementId, amountCents, description) => {
    const reference = `${order.id}::supplement::${supplementId}`;
    // No returnUrl here on purpose: the api derives it from the supplement reference (this
    // CMS has no REACT_APP_WEBSITE_URL, and shouldn't need the customer-facing website url).
    const response = await axios.post(`${resolveShopApiUrl(order.shopSource)}/api/payment/pay/checkout`, {
      description,
      totalPrice: amountCents,
      reference,
    });
    return response.data; // paymentUrl
  }, []);

  const cancelSupplementPayment = useCallback(async (order, supplementId, payTransactionId) => {
    // Token from the order's own Firebase project: each api verifies against its own project.
    const idToken = await getFirebaseIDTokenForShop(order.shopSource);
    await axios.patch(
      `${resolveShopApiUrl(order.shopSource)}/api/payment/pay/supplement/cancel`,
      { transactionId: payTransactionId, orderDocumentId: order.id, supplementId },
      { headers: { Authorization: idToken } },
    );
  }, []);

  const sendSupplementPaymentMail = useCallback(async (order, mail) => {
    await axios.post(`${resolveShopApiUrl(order.shopSource)}/api/email/order/supplement-payment`, mail);
  }, []);

  return {
    getOpenAdditionalPaymentSupplement,
    addHistoryEntry,
    createSupplement,
    updateSupplement,
    createPaymentLinkForSupplement,
    cancelSupplementPayment,
    sendSupplementPaymentMail,
  };
};
